/**
 * A Google Sheet by its link (Import a sheet). The browser fetches it from Google itself: Google lets Routr Dispatch's pages
 * read a sheet that's shared with anyone who has the link, so it never passes through Routr's server. A sheet that isn't
 * shared that way can't be read, and the owner is told how to share it, or to download it and choose the file.
 */

export interface SheetLink {
  /** The spreadsheet's id, or a published one's (`/spreadsheets/d/e/…`). */
  id: string;
  published: boolean;
  /** The tab the link names (`gid`), if it names one: only that tab is read. */
  gid: string | null;
}

/** What a pasted link points at, or null when it isn't a Google Sheet's. */
export function sheetLink(text: string): SheetLink | null {
  let u: URL;
  try {
    u = new URL(text.trim().replace(/^(?!https?:\/\/)/i, 'https://'));
  } catch {
    return null;
  }
  if (u.hostname !== 'docs.google.com') return null;
  const m = /^\/spreadsheets\/d\/(e\/)?([A-Za-z0-9_-]{20,})/.exec(u.pathname);
  if (!m) return null;
  const gid = u.searchParams.get('gid') ?? /(?:^#|[#&])gid=(\d+)/.exec(u.hash)?.[1] ?? null;
  return { id: m[2], published: !!m[1], gid: gid && /^\d+$/.test(gid) ? gid : null };
}

/** Where Google gives it: the whole spreadsheet as an .xlsx (its first sheet with rows is read), or the tab named, as a CSV. */
export function exportUrl(l: SheetLink): string {
  const base = `https://docs.google.com/spreadsheets/d/${l.published ? 'e/' : ''}${l.id}`;
  if (l.published) return l.gid ? `${base}/pub?gid=${l.gid}&single=true&output=csv` : `${base}/pub?output=xlsx`;
  return l.gid ? `${base}/export?format=csv&gid=${l.gid}` : `${base}/export?format=xlsx`;
}

export type LinkProblem = 'private' | 'missing' | 'offline';

/** The file's name as Google gives it ("Monday orders.xlsx"), from its Content-Disposition. */
export function fileNameOf(disposition: string | null): string | null {
  if (!disposition) return null;
  const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (star) {
    try {
      return decodeURIComponent(star.trim());
    } catch {
      // Fall back to the plain name.
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? null;
}

/** Fetches the sheet from Google: its bytes and its name, or why it couldn't. */
export async function fetchSheet(l: SheetLink, signal?: AbortSignal): Promise<{ bytes: Uint8Array; name: string } | LinkProblem> {
  let res: Response;
  try {
    res = await fetch(exportUrl(l), { credentials: 'omit', referrerPolicy: 'no-referrer', signal });
  } catch (e) {
    if (signal?.aborted) throw e;
    // A sheet that isn't shared sends the browser on to Google's sign-in, which a page can't read: from here it looks like
    // no answer at all, so it's said as not shared unless this computer is offline.
    return typeof navigator !== 'undefined' && navigator.onLine === false ? 'offline' : 'private';
  }
  if (res.status === 404 || res.status === 400) return 'missing';
  if (!res.ok) return res.status === 401 || res.status === 403 ? 'private' : 'offline';
  // Google's sign-in page, answered for a sheet that isn't shared.
  if (/text\/html/i.test(res.headers.get('content-type') ?? '')) return 'private';
  const bytes = new Uint8Array(await res.arrayBuffer());
  const name = fileNameOf(res.headers.get('content-disposition')) ?? (l.gid ? 'Google Sheet.csv' : 'Google Sheet.xlsx');
  return { bytes, name };
}
